/* eslint-disable react/no-unstable-nested-components */
import cn from "classnames";
import * as React from "react";
import { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import {
    Link,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    ActionButton,
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
    Tooltip,
} from "@jhool-io/fe-components";
import {
    buildSuccessMessage,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import {
    APP_COLORS,
    CPT_CODES,
    LISTS_DEFAULT_LIMIT,
} from "../../../../utils/constants";
import { useFetchClientBasicAndPersonalInfo } from "../../../../hooks/queries/client";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import useToast from "../../../../hooks/useToast";
import {
    BillStatus,
    IBillingResponse,
    PaymentModals,
} from "../../../billing/types/billing.types";

import ListState from "../../../../components/ListState/ListState";
import UnmatchedPaymentList from "../../../billing/components/UnmatchedPaymentList/UnmatchedPaymentList";
import InvoicePaymentForm from "./InvoicePaymentForm/InvoicePaymentForm";
import PaymentsOverview from "../../../billing/components/PaymentsOverview/PaymentsOverview";

import CreditCards from "../Insurance/CreditCards/CreditCards";
import { useFetchInvoices } from "../../../billing/hooks/queries/billing.queries";
import {
    getBillStatusFilterLabel,
    getBillStatusLabel,
    getBillStatusTag,
} from "../../../billing/helpers/billing.helpers";
import InfoCircleIcon from "../../../../components/Icons/InfoCircle";
import ChargeClient from "../../../billing/components/ChargeClientForm/ChargeClientForm";
import AddBulkWriteOffForm from "../../../billing/components/WriteOff/AddBulkWriteOffForm/AddBulkWriteOffForm";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { useUpdateInvoiceStatus } from "../../../payments/hooks/payments.mutations";
import PendingResolutionForm, {
    PendingResolutionMetaData,
} from "../../../billing/components/PendingResolutionForm/PendingResolutionForm";
import useShowInsuranceOverSelfPay from "../../../practice-settings/hooks/useShowInsuranceOverSelfPay";
import { NoteTypes } from "../../../notes/types/notes.types";
import { getCptCodeToDisplay } from "../../../notes/helpers/notes.helpers";
import UpdateForm from "../../../billing/components/UpdateForm/UpdateForm";
import PaymentBreakdown from "../../../billing/components/PaymentBreakdown/PaymentBreakdown";
import styles from "./ClientClaimsList.module.scss";
import Loader from "../../../../shared-ui/Loader/Loader";
import InsuranceProviderFilter from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import ChevronUpIcon from "../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import InfoIcon from "../../../../icons/Info";
import EditIcon from "../../../../icons/Edit";
import {
    useCanSetAnyClientBiller,
    useCanSetClientBiller,
    useGetClientCanByChargedByText,
    usePendingResolutionDialogText,
    useSortedBillStatuses,
} from "../../../billing/hooks/helpers.hooks";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useFetchUsersList } from "../../../user-management/hooks/user-management.queries";
import { useDebounce } from "../../../../hooks/helpers";
import CheckIcon from "../../../../icons/Check";
import { useEditClientPersonalInfo } from "../../../../hooks/mutations/client";
import { IClientEditPersonalInfo } from "../../../../utils/types/client";
import { getStatusTag } from "../../../claims/helpers/claims.helper";
import PaymentLinkPreviewForm from "../ClientPaymentLinkPreview/ClientPaymentLinkPreview";
import { useGeneratePaymentLink } from "../../hooks/clients.mutations";
import ClientPaymentLinkGenerated from "../ClientPaymentLinkGenerated/ClientPaymentLinkGenerated";
import {
    PaymentLinkType,
    IGeneratePaymentLinkInvoicePayload,
    IGeneratePaymentLinkResponse,
} from "../../types/clients.types";

interface ISubComponent {
    // Eslint is misbehaving here giving a false error, so adding this.
    // eslint-disable-next-line react/no-unused-prop-types
    row: Row<IBillingResponse>;
}

export default function ClientClaimsList() {
    const [rowSelection, setRowSelection] = React.useState({});
    const [expandedRows, setExpandedRows] = React.useState<ExpandedState>({});
    const [modalInView, setModalInView] = React.useState<PaymentModals | null>(
        null
    );
    const [propsForPendingResolution, setPropsForPendingResolution] =
        React.useState<PendingResolutionMetaData | null>(null);
    const [showPendingReolutionDialog, setShowPendingResolutionDialog] =
        React.useState(false);
    const [getSelectedData, setGetSelectedData] =
        React.useState<Row<IBillingResponse>[]>();
    const [showChargeModal, setShowChargeModal] = React.useState(false);
    const [invoiceToUpdateId, setInvoiceToUpdateId] = React.useState<
        string | null
    >(null);
    const [insuranceProviderIdsFilter, setInsuranceProviderIdsFilter] =
        React.useState<string[]>([]);
    const [showPaymentLinkPreviewDialog, setShowPaymentLinkPreviewDialog] =
        React.useState(false);
    const [showPaymentLinkSuccessDialog, setShowPaymentLinkSuccessDialog] =
        React.useState(false);
    const [showPaymentLinkErrorDialog, setShowPaymentLinkErrorDialog] =
        React.useState(false);
    const [paymentLinkData, setPaymentLinkData] =
        React.useState<IGeneratePaymentLinkResponse | null>(null);
    const [paymentLinkErrorMessage, setPaymentLinkErrorMessage] =
        React.useState<string | undefined>(undefined);
    const [selectedLinkType, setSelectedLinkType] =
        React.useState<PaymentLinkType | null>(null);
    const [isPaymentLinkFormValid, setIsPaymentLinkFormValid] =
        React.useState<boolean>(true);

    const [searchParams, setSearchParams] = useSearchParams();

    const [editingInsuranceRowId, setEditingInsuranceRowId] = React.useState<
        string | null
    >();

    const [insuranceChargeAmount, setInsuranceChargeAmount] = React.useState(0);
    const [editingCoInsuranceRowId, setEditingCoInsuranceRowId] =
        React.useState<string | null>();
    const [internalUserSearchString, setInternalUserSearchString] =
        React.useState("");
    const debouncedUserSearchString = useDebounce(
        internalUserSearchString,
        500
    );

    const [coInsuranceChargeAmount, setCoInsuranceChargeAmount] =
        React.useState(0);
    const [showInternalUsersListPopover, setShowInternalUsersListPopover] =
        React.useState(false);

    // Get id from url params
    const params = useParams();
    const clientId = params.clientId as string;

    const navigate = useNavigate();

    // check if logged in user has permission to perform payment actions
    const canUserPerformAction = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INVOICE_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const clientCanBeChargedByText = useGetClientCanByChargedByText();

    const loggedInUser = useFetchUserDetails();

    const clientDetails = useFetchClientBasicAndPersonalInfo(clientId);
    const { mutate, isLoading: isEditingClientDetails } =
        useEditClientPersonalInfo(clientId);

    const generatePaymentLink = useGeneratePaymentLink();

    const canSetAnyClientBiller = useCanSetAnyClientBiller();

    const canSetClientBiller = useCanSetClientBiller();

    const canEditClientBiller = clientDetails?.data?.data
        ?.client_can_be_charged_by?.user_id
        ? canSetAnyClientBiller ||
          loggedInUser?.data?.user_id ===
              clientDetails?.data?.data?.client_can_be_charged_by?.user_id
        : canSetClientBiller;

    const internalUsersList = useFetchUsersList(
        {
            search_string: debouncedUserSearchString.toLowerCase(),
            roles: [
                UserRole.BILLER,
                UserRole.BILLER_ADMIN,
                UserRole.SUPER_ADMIN,
            ],
        },
        canEditClientBiller
    );

    const canSeeChargeButtons =
        clientDetails?.data?.data?.client_can_be_charged_by?.user_id ===
            loggedInUser?.data?.user_id ||
        !clientDetails?.data?.data?.client_can_be_charged_by?.user_id;

    const filteredInternalUsersList =
        internalUsersList?.data?.data
            ?.filter((user) => {
                if (!canSetAnyClientBiller) {
                    return user.user_id === loggedInUser?.data?.user_id;
                }
                return true;
            })
            ?.sort((a, b) => {
                if (a.user_id === loggedInUser?.data?.user_id) return -1;
                if (b.user_id === loggedInUser?.data?.user_id) return 1;
                return 0;
            }) || [];

    const getBillStatus = () => {
        const invoiceStatusFilter = searchParams.getAll("invoice_status[]");

        if (invoiceStatusFilter.length > 0) {
            if (invoiceStatusFilter.includes("all")) {
                return [];
            }
            return invoiceStatusFilter;
        }

        return [];
    };

    const selectedBills = getSelectedData?.map((item) => item.original);

    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const cptCodeFilter = searchParams.get("cpt_code") || "";
    // const invoiceStatusFilter = getBillStatus() || "";
    const invoiceStatusFilter = getBillStatus() || [];
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const billNavigationState = React.useMemo(() => {
        const navParams = new URLSearchParams(searchParams);
        navParams.set("client_id", clientId);
        if (navParams.getAll("invoice_status[]").length === 0) {
            navParams.append("invoice_status[]", "all");
        }
        insuranceProviderIdsFilter.forEach((insuranceProviderId) =>
            navParams.append("insurance_provider_ids[]", insuranceProviderId)
        );
        navParams.set("page", "1");
        return navParams.toString();
    }, [searchParams, clientId, insuranceProviderIdsFilter]);

    const canViewCreditCardInfo = useDoesUserHaveRole([
        UserRole.SUPER_ADMIN,
        UserRole.CLIENT_SUPPORT,
        UserRole.SUPPORT_GENERAL,
    ]);

    // check if logged in user can perform bulk actions
    const canPerformBulkActions = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_PAYMENTS_WRITE,
        UserPermisions.SUPPORT_PAYMENT_CORRECTION_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const queryClient = useQueryClient();

    // Toast for success and error states
    const { toast } = useToast();

    const updateInvoice = useUpdateInvoiceStatus();

    /**
     * Get client charge failed modal in view
     * Should be "prompt" | "pr" | null
     */
    const chargeModalInView = searchParams.get("cf_modal");

    const canSplitPayment = searchParams.get("split_payment");

    const chargeClientIsMutating = useIsMutating(["charge-client"]);
    const updateInVoiceIsMutating = useIsMutating(["update-invoice"]);
    const invoicePaymentIsMutating = useIsMutating(["bulk-pay-invoices"]);
    const bulkWriteOffIsMutating = useIsMutating(["bulk-invoice-payment"]);
    const generatePaymentLinkIsMutating = useIsMutating([
        "generate-payment-link",
    ]);

    const paymentIsMutating = useIsMutating([
        "add-payment",
        selectedBills ? selectedBills[0]?.invoice_id : "",
    ]);

    const pendingResolutionDialogText = usePendingResolutionDialogText();

    const allowedRolesForUpdatingCharge = [
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
        UserRole.SUPER_ADMIN,
    ];

    const canUpdateChargeAmount = useDoesUserHaveRole(
        allowedRolesForUpdatingCharge
    );

    const handleModalInView = (obj: { modal: PaymentModals | null }) => {
        setModalInView(obj.modal);

        setSearchParams(searchParams);
    };

    // Hook for fetching all invoice
    const { data, isLoading, isSuccess, error } = useFetchInvoices({
        cpt_code: cptCodeFilter,
        invoice_status: invoiceStatusFilter,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        page: pageFilter,
        limit: limitFilter,
        client_id: clientId,
        insurance_provider_ids: insuranceProviderIdsFilter,
    });

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleStatusChange = (status: string) => {
        if (invoiceStatusFilter.includes(status)) {
            searchParams.delete("invoice_status[]", status);
        } else {
            searchParams.append("invoice_status[]", status);
        }
        localStorage.setItem(
            "clientclaimslist_filters",
            searchParams.toString()
        );

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleCptCodeChange = (value: string) => {
        if (value === "") searchParams.delete("cpt_code");
        else searchParams.set("cpt_code", value);
        localStorage.setItem(
            "clientclaimslist_filters",
            searchParams.toString()
        );
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        localStorage.setItem(
            "clientclaimslist_filters",
            searchParams.toString()
        );
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        localStorage.setItem(
            "clientclaimslist_filters",
            searchParams.toString()
        );
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const chargeDialogText =
        chargeModalInView !== "pr"
            ? "Charge client"
            : pendingResolutionDialogText;

    const handleGeneratePaymentLink = (linkType: PaymentLinkType) => {
        if (!getSelectedData || getSelectedData.length === 0) {
            toast({
                mode: "error",
                message: "Please select at least one invoice",
            });
            return;
        }

        setSelectedLinkType(linkType);
        setShowPaymentLinkPreviewDialog(true);
    };

    const handlePreviewGenerate = (
        invoices: IGeneratePaymentLinkInvoicePayload[],
        email?: string
    ) => {
        if (
            !getSelectedData ||
            getSelectedData.length === 0 ||
            !selectedLinkType
        ) {
            return;
        }

        setShowPaymentLinkPreviewDialog(false);
        setShowPaymentLinkSuccessDialog(true);

        generatePaymentLink.mutate(
            {
                invoices,
                link_type: selectedLinkType,
                email,
            },
            {
                onSuccess: (response) => {
                    setPaymentLinkData(
                        response.data as IGeneratePaymentLinkResponse
                    );
                },
                onError: (err) => {
                    setShowPaymentLinkSuccessDialog(false);
                    const message =
                        (err.response?.data as { message?: string })?.message ||
                        "Failed to generate payment link. Please try again.";
                    setPaymentLinkErrorMessage(message);
                    setShowPaymentLinkErrorDialog(true);
                },
            }
        );
    };

    const handleMessageToDisplay = () => {
        const clientName = displayNameInRightFormat({
            firstName: clientDetails.data?.data.first_name || "",
            lastName: clientDetails.data?.data.last_name || "",
        });

        if (fromDateFilterToDate && toDateFilterToDate) {
            return (
                <h4 className="text-base font-semibold mb-[--spacing-header]">
                    {`Showing bills for  ${
                        clientDetails.isLoading ? "" : clientName
                    } between ${format(
                        fromDateFilterToDate,
                        "MMM d, yyyy"
                    )} and ${format(toDateFilterToDate, "MMM d, yyyy")} . `}
                </h4>
            );
        }

        if (fromDateFilterToDate) {
            return (
                <h4 className="text-base font-semibold mb-[--spacing-header]">
                    {`Showing bills for  ${
                        clientDetails.isLoading ? "" : clientName
                    } from ${format(fromDateFilterToDate, "MMM d, yyyy")}.`}
                </h4>
            );
        }

        if (toDateFilterToDate) {
            return (
                <h4 className="text-base font-semibold mb-[--spacing-header]">
                    {`Showing bills for  ${
                        clientDetails.isLoading ? "" : clientName
                    } to ${format(toDateFilterToDate, "MMM d, yyyy")}.`}
                </h4>
            );
        }

        return (
            <h4 className="text-base font-semibold mb-[--spacing-header]">
                {`Showing all bills for ${
                    clientDetails.isLoading ? "" : clientName
                }. `}
            </h4>
        );
    };

    const handleGenerateStatement = () => {
        if (fromDateFilter && toDateFilter) {
            navigate(
                `/reports/billing-reports/${clientId}?from_date=${searchParams.get(
                    "from_date"
                )}&to_date=${searchParams.get("to_date")}`
            );
        } else if (fromDateFilter) {
            navigate(
                `/reports/billing-reports/${clientId}?from_date=${searchParams.get(
                    "from_date"
                )}`
            );
        } else if (toDateFilter) {
            navigate(
                `/reports/billing-reports/${clientId}?to_date=${searchParams.get(
                    "to_date"
                )}`
            );
        } else {
            navigate(`/reports/billing-reports/${clientId}`);
        }
    };

    // Function to charge modal search params
    const removeChargeModalParams = () => {
        searchParams.delete("cf_modal");
        setSearchParams(searchParams);
    };

    const handleClearIssue = () => {
        searchParams.delete("payment_failed");
        setSearchParams(searchParams);
    };

    const handleChargeDialogClose = () => {
        handleModalInView({ modal: null });
        setRowSelection({});
        handleClearIssue();

        if (chargeModalInView) {
            removeChargeModalParams();
        }
    };

    const handleCloseUnmatchPaymentsModal = () => {
        searchParams.delete("unmatch_page");
        searchParams.delete("payment_status");
        searchParams.delete("unmatch_limit");
        searchParams.delete("split_payment");
        handleModalInView({ modal: null });

        setSearchParams();
    };

    const handleRowStyle = (row: Row<IBillingResponse>) => {
        if (
            row.original.insurance_amount > 0 &&
            row.original.accounting_insurance < 0
        ) {
            return {
                backgroundColor: "#ffebed",
                borderBottom: "1px solid #ECEEF2",
            };
        }
        if (
            row.original.insurance_amount === 0 &&
            row.original.accounting_coinsurance < 0
        ) {
            return {
                backgroundColor: "#ffebed",
                borderBottom: "1px solid #ECEEF2",
            };
        }
        return {
            backgroundColor: "var(--bg-table)",
            borderBottom: "1px solid #ECEEF2",
        };
    };

    const renderRowSubComponentShow = React.useCallback(
        ({ row }: ISubComponent) => (
            <PaymentBreakdown
                row={row.original}
                isExpanded={row.getIsExpanded()}
            />
        ),
        []
    );

    const getOutstandingValueToDisplay = (row: Row<IBillingResponse>) => {
        const coInsurance = row.original.accounting_coinsurance;
        const insurancePending =
            row.original.patient_insurance_pending_settlement;

        if (coInsurance <= 0) {
            if (insurancePending && insurancePending > 0) {
                return (
                    <span className="flex items-center">
                        {showMoneyInAppFormat(Math.abs(coInsurance))} +{" "}
                        {showMoneyInAppFormat(insurancePending)}
                        <Tooltip content="Insurance balance transferred to patient">
                            <Button variant="normal" size="auto">
                                <InfoCircleIcon
                                    stroke={APP_COLORS.COLOR_BLACK}
                                    fill={APP_COLORS.COLOR_BLACK}
                                />
                            </Button>
                        </Tooltip>
                    </span>
                );
            }
            return showMoneyInAppFormat(Math.abs(coInsurance));
        }

        if (coInsurance > 0) {
            if (insurancePending && insurancePending > 0) {
                return (
                    <span className="flex items-center text-danger">
                        ({showMoneyInAppFormat(coInsurance)}) +{" "}
                        {showMoneyInAppFormat(insurancePending)}
                        <Tooltip content="Insurance balance transferred to patient">
                            <Button variant="normal" size="auto">
                                <InfoCircleIcon
                                    stroke={APP_COLORS.COLOR_BLACK}
                                    fill={APP_COLORS.COLOR_BLACK}
                                />
                            </Button>
                        </Tooltip>
                    </span>
                );
            }
            return (
                <span className="text-danger">
                    ({showMoneyInAppFormat(coInsurance)})
                </span>
            );
        }

        return "--";
    };

    const handleShowPendingResolutionDialog = (row?: Row<IBillingResponse>) => {
        setShowPendingResolutionDialog(true);
        setPropsForPendingResolution({
            noteId: row?.original.note_id,
            clientId: row?.original.client.client_id,
            invoiceId: row?.original.invoice_id,
            invoiceIds: getSelectedData
                ? getSelectedData.map((item) => item.original.invoice_id)
                : undefined,
        });
    };

    const handleClosePendingResolutionDialog = () => {
        setShowPendingResolutionDialog(false);
        setPropsForPendingResolution(null);
        handleClearIssue();
    };

    const sortedBillStatuses = useSortedBillStatuses();

    // function to update invoice status
    const handleUpdateInvoiceStatus = (
        status: BillStatus,
        invoiceId?: string
    ) => {
        const selectedIds = getSelectedData
            ? getSelectedData
                  .filter((item) => Boolean(item.original.invoice_id))
                  .map((item) => item.original.invoice_id)
            : [];

        const idsToChange = invoiceId ? [invoiceId] : selectedIds;

        const changeStatusPayload = {
            invoice_ids: idsToChange,
            status,
        };
        updateInvoice.mutate(changeStatusPayload, {
            onSuccess: async (response) => {
                await queryClient.invalidateQueries({
                    queryKey: ["invoices"],
                });
                toast({
                    mode: "success",
                    message: `${buildSuccessMessage(
                        response.data.updated_invoices,
                        response.data.unupdated_invoices
                    )}`,
                });
                if (selectedIds) setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err?.response?.data.message ||
                        "Cannot update bill status at this time",
                });
            },
        });
    };

    const handleBillStatusSelect = (status: BillStatus) => {
        if (status === BillStatus.PENDING_RESOLUTION) {
            handleShowPendingResolutionDialog();
            return;
        }
        handleUpdateInvoiceStatus(status);
    };

    const handleUpdateClientDetails = async (userId: string | null) => {
        if (clientDetails?.data?.data) {
            let userIdToSend: string | null | undefined;

            if (userId) {
                if (
                    userId !==
                    clientDetails?.data?.data?.client_can_be_charged_by?.user_id
                ) {
                    userIdToSend = userId;
                } else {
                    userIdToSend = undefined;
                }
            } else {
                userIdToSend = null;
            }

            const dataToSend: IClientEditPersonalInfo = {
                first_name: clientDetails?.data?.data?.first_name,
                last_name: clientDetails?.data?.data?.last_name,
                pronouns: clientDetails?.data?.data?.pronouns,
                user_contact_phone_no: clientDetails?.data?.data?.contact,
                financial_assistance_status:
                    clientDetails?.data?.data?.financial_assistance_status,
                address: clientDetails?.data?.data?.address,
                user_email: clientDetails?.data?.data?.email,
                client_status: clientDetails?.data?.data?.client_status,
                consent_to_sms:
                    clientDetails?.data?.data?.consent_to_sms || null,
                client_can_be_charged_by: userIdToSend,
            };

            mutate(dataToSend, {
                onSuccess: (result) => {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, "basic-personal-info"],
                    });
                    toast({
                        mode: "success",
                        message: result.message || "Changes saved!",
                    });
                    setShowInternalUsersListPopover(false);
                },
                onError: () => {
                    toast({
                        mode: "error",
                        message:
                            error?.response?.data.message ||
                            "Couldn't save changes!",
                    });
                },
            });
        }
    };

    const cptCodeSectionDisplay = (
        row: Row<IBillingResponse>,
        hasDifferentBackground: boolean
    ) => {
        return (
            <span
                className={
                    hasDifferentBackground ? "text-[#0b132b]" : "text-text"
                }
            >
                {getCptCodeToDisplay({
                    noteType: row.original.note_type as NoteTypes,
                    cptCode: row.original.cpt_code,
                    unitCalculation:
                        row.original.note_metadata?.unit_calculation,
                })}
            </span>
        );
    };

    const columns: ColumnDef<IBillingResponse>[] = React.useMemo(() => {
        const hasDifferentBackground = (row: Row<IBillingResponse>) => {
            return (
                (row.original.insurance_amount > 0 &&
                    row.original.accounting_insurance < 0) ||
                (row.original.insurance_amount === 0 &&
                    row.original.accounting_coinsurance < 0)
            );
        };
        return [
            {
                id: "invoice_id",
                header: ({ table }) => (
                    <Checkbox
                        handleChange={table.getToggleAllRowsSelectedHandler()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={table.getIsAllRowsSelected()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                ),
            },
            {
                accessorKey: "date_of_service",
                header: "DATE OF SERVICE",
                cell: ({ row }) => (
                    <div
                        className={cn("flex flex-col gap-y-1 text-text", {
                            "text-[#0b132b]": hasDifferentBackground(row),
                        })}
                    >
                        <div className="flex gap-[--spacing-items]">
                            <Button
                                variant="normal"
                                size="auto"
                                onClick={() => {
                                    row.toggleExpanded();

                                    row.getToggleExpandedHandler();
                                }}
                                className={cn(
                                    "size-4 [border:1px_solid_var(--color-text)] rounded-full",
                                    {
                                        "[border:1px_solid_#0b132b]":
                                            hasDifferentBackground(row),
                                    }
                                )}
                            >
                                {row.getIsExpanded() ? (
                                    <ChevronUpIcon
                                        className={
                                            hasDifferentBackground(row)
                                                ? "text-[#0b132b]"
                                                : "stroke-text"
                                        }
                                    />
                                ) : (
                                    <ChevronDownIcon
                                        className={
                                            hasDifferentBackground(row)
                                                ? "text-[#0b132b]"
                                                : "stroke-text"
                                        }
                                    />
                                )}
                            </Button>

                            <Link
                                to={`/billing-and-claims/${row.original.client.client_id}/${row.original.note_id}`}
                                state={billNavigationState}
                                className={cn("text-textLink underline", {
                                    "!text-primary":
                                        hasDifferentBackground(row),
                                })}
                            >
                                {formatDate(row.original.date_of_service) ||
                                    "--"}
                            </Link>
                        </div>

                        <span
                            className={
                                hasDifferentBackground(row)
                                    ? "text-[#0b132b]"
                                    : "text-text"
                            }
                        >
                            {`Bill: `}
                            {getBillStatusTag(
                                row.original.invoice_status as BillStatus
                            )}
                        </span>
                    </div>
                ),
            },

            {
                accessorKey: "cpt_code",
                header: "CPT CODE",
                cell: ({ row }) => {
                    return (
                        <div
                            className={cn("flex flex-col items-start gap-y-1")}
                        >
                            {cptCodeSectionDisplay(
                                row,
                                hasDifferentBackground(row)
                            )}
                            <span
                                className={
                                    hasDifferentBackground(row)
                                        ? "text-[#0b132b]"
                                        : "text-text"
                                }
                            >
                                {`Claim: `}
                                {row.original.active_claim_status
                                    ? getStatusTag(
                                          row.original.active_claim_status
                                      )
                                    : "--"}
                            </span>
                        </div>
                    );
                },
            },

            {
                accessorKey: "pos_code",
                header: "pos",
                cell: ({ row }) => {
                    const regex = /^(\d+) - (.+)$/;

                    if (row.original.pos_code) {
                        return (
                            <div className="flex flex-col items-start gap-y-1">
                                <ActionButton
                                    variant="normal"
                                    size="auto"
                                    iconRight={<InfoIcon />}
                                    actionTitle={
                                        row.original.pos_code.match(
                                            regex
                                        )?.[2] || ""
                                    }
                                    className={
                                        hasDifferentBackground(row)
                                            ? "text-[#0b132b]"
                                            : "text-text"
                                    }
                                >
                                    {row.original.pos_code.match(regex)?.[1] ||
                                        ""}
                                </ActionButton>
                                {row.original.is_note_archived ? (
                                    <Badge
                                        type="info"
                                        content="Archived"
                                        className="self-start"
                                    />
                                ) : null}
                            </div>
                        );
                    }
                    return (
                        <div className="flex flex-col items-start gap-y-1">
                            <span
                                className={
                                    hasDifferentBackground(row)
                                        ? "text-[#0b132b]"
                                        : "text-text"
                                }
                            >
                                --
                            </span>
                            {row.original.is_note_archived ? (
                                <Badge
                                    type="info"
                                    content="Archived"
                                    className="self-start"
                                />
                            ) : null}
                        </div>
                    );
                },
            },
            {
                accessorKey: "insurance_provider_name",
                header: "Ins",
                cell: ({ row }) => (
                    <span
                        className={cn("uppercase text-text", {
                            "normal-case":
                                row.original.metadata?.self_pay &&
                                !showInsOverSelfPay,
                            "!text-[#0b132b]": hasDifferentBackground(row),
                        })}
                    >
                        {row.original.metadata?.self_pay && !showInsOverSelfPay
                            ? "Self pay"
                            : (row.original.insurance_provider &&
                                  removeEnumUnderscore(
                                      row.original.insurance_provider || ""
                                  )) ||
                              "--"}
                    </span>
                ),
            },
            {
                accessorKey: "note_author",
                header: "CLINICIAN",
                cell: ({ row }) => (
                    <span
                        className={
                            hasDifferentBackground(row)
                                ? "text-[#0b132b]"
                                : "text-text"
                        }
                    >
                        {row.original.note_author
                            ? displayNameInRightFormat({
                                  firstName:
                                      row.original.note_author.first_name,
                                  lastName: row.original.note_author.last_name,
                              })
                            : "--"}
                    </span>
                ),
            },
            {
                accessorKey: "interest_collected",
                header: "Interest Payment",
                cell: ({ row }) => (
                    <span
                        className={
                            hasDifferentBackground(row)
                                ? "text-[#0b132b]"
                                : "text-text"
                        }
                    >
                        {typeof row.original.interest_collected === "number"
                            ? showMoneyInAppFormat(
                                  row.original.interest_collected
                              )
                            : "--"}
                    </span>
                ),
            },
            {
                header: "INSURANCE",
                columns: [
                    {
                        accessorKey: "charge",
                        header: "CHG",
                        cell: ({ row }) => (
                            <div className="flex items-center gap-2">
                                {editingInsuranceRowId ===
                                row.original.invoice_id ? (
                                    <div>
                                        <UpdateForm
                                            invoiceId={
                                                invoiceToUpdateId as string
                                            }
                                            charge_amount={
                                                insuranceChargeAmount || 0
                                            }
                                            selectedChargeType="insurance"
                                            setEditingRowId={
                                                setEditingInsuranceRowId
                                            }
                                            updateType="charge_amount"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex gap-1 items-center">
                                        <span>
                                            {showMoneyInAppFormat(
                                                row.original.insurance_amount
                                            )}
                                        </span>
                                        {canUpdateChargeAmount &&
                                            row.original.invoice_status !==
                                                BillStatus.CLOSED && (
                                                <ActionButton
                                                    size="auto"
                                                    variant="normal"
                                                    onClick={() => {
                                                        setInsuranceChargeAmount(
                                                            row.original
                                                                .insurance_amount
                                                        );
                                                        setInvoiceToUpdateId(
                                                            row.original
                                                                .invoice_id
                                                        );
                                                        setEditingInsuranceRowId(
                                                            row.original
                                                                .invoice_id
                                                        );
                                                    }}
                                                    actionTitle="Update charge"
                                                >
                                                    <EditIcon className="stroke-[rgb(11,19,43)]" />
                                                </ActionButton>
                                            )}
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    {
                        accessorKey: "collected",
                        header: "COL",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(
                                row.original.insurance_amount_collected
                            ),
                    },
                    {
                        accessorKey: "balance",
                        header: "OUT",
                        cell: ({ row }) => (
                            <span
                                className={cn("text-base", {
                                    "text-danger":
                                        row.original.accounting_insurance > 0,
                                })}
                            >
                                {row.original.accounting_insurance > 0
                                    ? `(${showMoneyInAppFormat(
                                          row.original.accounting_insurance
                                      )})`
                                    : showMoneyInAppFormat(
                                          Math.abs(
                                              row.original.accounting_insurance
                                          )
                                      )}
                            </span>
                        ),
                    },
                ],
            },

            {
                header: "CO-INSURANCE",
                columns: [
                    {
                        accessorKey: "charge",
                        header: "CHG",
                        cell: ({ row }) => (
                            <div className="flex items-center gap-2">
                                {editingCoInsuranceRowId ===
                                row.original.invoice_id ? (
                                    <div>
                                        <UpdateForm
                                            invoiceId={
                                                invoiceToUpdateId as string
                                            }
                                            charge_amount={
                                                coInsuranceChargeAmount || 0
                                            }
                                            selectedChargeType="coinsurance"
                                            setEditingRowId={
                                                setEditingCoInsuranceRowId
                                            }
                                            updateType="charge_amount"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex gap-1 items-center">
                                        <span>
                                            {showMoneyInAppFormat(
                                                row.original.coinsurance_amount
                                            )}
                                        </span>
                                        {canUpdateChargeAmount &&
                                            row.original.invoice_status !==
                                                BillStatus.CLOSED && (
                                                <ActionButton
                                                    size="auto"
                                                    variant="normal"
                                                    onClick={() => {
                                                        setCoInsuranceChargeAmount(
                                                            row.original
                                                                .coinsurance_amount
                                                        );
                                                        setInvoiceToUpdateId(
                                                            row.original
                                                                .invoice_id
                                                        );
                                                        setEditingCoInsuranceRowId(
                                                            row.original
                                                                .invoice_id
                                                        );
                                                    }}
                                                    actionTitle="Update charge"
                                                >
                                                    <EditIcon className="stroke-[rgb(11,19,43)]" />
                                                </ActionButton>
                                            )}
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    {
                        accessorKey: "collected",
                        header: "COL",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(
                                row.original.coinsurance_amount_collected
                            ),
                    },
                    {
                        accessorKey: "balance",
                        header: "OUT",
                        cell: ({ row }) => getOutstandingValueToDisplay(row),
                    },
                ],
            },
            {
                header: "PR100",
                columns: [
                    {
                        accessorKey: "charge",
                        header: "CHg",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(row.original.pr100_amount),
                    },
                    {
                        accessorKey: "pr100_collected",
                        header: "COL",
                        cell: ({ row }) =>
                            showMoneyInAppFormat(row.original.pr100_collected),
                    },
                    {
                        accessorKey: "balance",
                        header: "OUT",
                        cell: ({ row }) => (
                            <span
                                className={cn("text-base", {
                                    "text-danger":
                                        row.original.accounting_pr100 < 0,
                                })}
                            >
                                {row.original.accounting_pr100 < 0
                                    ? `(${showMoneyInAppFormat(
                                          Math.abs(
                                              row.original.accounting_pr100
                                          )
                                      )})`
                                    : showMoneyInAppFormat(
                                          row.original.accounting_pr100
                                      )}
                            </span>
                        ),
                    },
                ],
            },
            {
                accessorKey: "metadata",
                header: "AA",
                cell: ({ row }) => {
                    if (
                        !row.original?.metadata ||
                        !row.original.metadata.accepts_assignment
                    ) {
                        return "--";
                    }
                    if (row.original.metadata.accepts_assignment) {
                        return <Badge content="Yes" type="success" />;
                    }

                    return <Badge content="No" type="danger" />;
                },
            },
        ];
    }, [
        billNavigationState,
        canUpdateChargeAmount,
        coInsuranceChargeAmount,
        editingCoInsuranceRowId,
        editingInsuranceRowId,
        insuranceChargeAmount,
        invoiceToUpdateId,
        showInsOverSelfPay,
    ]);

    return (
        <>
            {showPendingReolutionDialog && (
                <Dialog open={showPendingReolutionDialog}>
                    <DialogContent
                        title={pendingResolutionDialogText}
                        handleCloseDialog={handleClosePendingResolutionDialog}
                        variant="center"
                        showFooter
                        cancelText="Cancel"
                        saveText={pendingResolutionDialogText}
                        submitBtnFormValue="pending-resolution"
                        isSubmitBtnDisabled={updateInVoiceIsMutating > 0}
                        isCancelBtnDisabled={updateInVoiceIsMutating > 0}
                    >
                        <PendingResolutionForm
                            onFormSubmit={() => {
                                handleClosePendingResolutionDialog();
                                setRowSelection({});
                            }}
                            metaProps={propsForPendingResolution || undefined}
                        />
                    </DialogContent>
                </Dialog>
            )}
            <Dialog open={modalInView === "bulk_writeoff"}>
                <DialogContent
                    handleCloseDialog={() => setModalInView(null)}
                    title="Bulk Write-Off"
                    variant="center"
                    saveText="Bulk Write-Off"
                    submitBtnFormValue="bulk-writeoff-form"
                    isSubmitBtnDisabled={bulkWriteOffIsMutating > 0}
                    isCancelBtnDisabled={bulkWriteOffIsMutating > 0}
                >
                    <AddBulkWriteOffForm
                        onFormSubmit={() => setModalInView(null)}
                    />
                </DialogContent>
            </Dialog>
            {selectedBills && selectedBills[0]?.invoice_id && (
                <Dialog open={modalInView === "unmatched_payments"}>
                    <DialogContent
                        title="Unmatched Payments"
                        showFooter={false}
                        handleCloseDialog={handleCloseUnmatchPaymentsModal}
                        wrapperClass={cn("w-[900px]", {
                            "w-[1500px]": canSplitPayment,
                        })}
                    >
                        <UnmatchedPaymentList
                            invoiceBalance={
                                selectedBills[0].accounting_coinsurance || 0
                            }
                            invoiceId={selectedBills[0].invoice_id}
                            onFormSubmit={handleCloseUnmatchPaymentsModal}
                            dateOfService={
                                selectedBills[0].date_of_service || ""
                            }
                        />
                    </DialogContent>
                </Dialog>
            )}

            {selectedBills && selectedBills[0]?.invoice_id && (
                <Dialog open={modalInView === "charge_client"}>
                    <DialogContent
                        variant="center"
                        showFooter={
                            !chargeModalInView || chargeModalInView === "pr"
                        }
                        submitBtnFormValue={
                            chargeModalInView === "pr"
                                ? "pending-resolution"
                                : "charge-client"
                        }
                        handleCloseDialog={handleChargeDialogClose}
                        saveText={chargeDialogText}
                        isSubmitBtnDisabled={
                            chargeClientIsMutating > 0 ||
                            updateInVoiceIsMutating > 0
                        }
                        isCancelBtnDisabled={
                            chargeClientIsMutating > 0 ||
                            updateInVoiceIsMutating > 0
                        }
                        title={chargeDialogText}
                        isBackBtnDisabled={paymentIsMutating > 0}
                    >
                        <ChargeClient
                            onFormSubmit={handleChargeDialogClose}
                            invoiceId={selectedBills[0].invoice_id}
                            noteId={
                                selectedBills &&
                                selectedBills.length === 1 &&
                                selectedBills[0].note_id
                                    ? selectedBills[0].note_id
                                    : ""
                            }
                        />
                    </DialogContent>
                </Dialog>
            )}

            <Dialog open={showChargeModal}>
                <DialogContent
                    variant="center"
                    title="Charge invoices"
                    handleCloseDialog={() => {
                        setShowChargeModal(false);
                        setRowSelection({});
                    }}
                    showFooter
                    saveText="Charge invoices"
                    submitBtnFormValue="invoice-payment"
                    isSubmitBtnDisabled={invoicePaymentIsMutating > 0}
                    isCancelBtnDisabled={invoicePaymentIsMutating > 0}
                >
                    <InvoicePaymentForm
                        onFormSubmit={() => {
                            setShowChargeModal(false);
                            setRowSelection({});
                        }}
                        invoices={selectedBills || []}
                    />
                </DialogContent>
            </Dialog>

            <div>
                <PaymentsOverview />

                <Card classNames="my-[--spacing-header]">
                    {handleMessageToDisplay()}

                    <div className="flex gap-[--spacing-items] flex-wrap justify-between">
                        <div className="flex gap-[--spacing-items] flex-wrap">
                            <Button
                                variant="normal"
                                size="auto"
                                className="[border:0.5px_solid_var(--color-stroke)] text-text hover:bg-interactionHover h-8 px-2 shadow-active"
                                onClick={() => handleGenerateStatement()}
                            >
                                {fromDateFilter || toDateFilter
                                    ? "Generate account statement for selected period"
                                    : "Generate account statement"}
                            </Button>

                            {selectedBills && selectedBills.length === 1 && (
                                <Button
                                    variant="normal"
                                    size="auto"
                                    className="[border:0.5px_solid_var(--color-stroke)] text-text hover:bg-interactionHover h-8 px-3 shadow-active"
                                    onClick={() =>
                                        handleModalInView({
                                            modal: "unmatched_payments",
                                        })
                                    }
                                >
                                    Check unmatched payments
                                </Button>
                            )}
                            {canUserPerformAction ? (
                                <Button
                                    variant="normal"
                                    size="auto"
                                    className="[border:0.5px_solid_var(--color-stroke)] text-text hover:bg-interactionHover h-8 px-3 shadow-active"
                                    onClick={() =>
                                        handleModalInView({
                                            modal: "bulk_writeoff",
                                        })
                                    }
                                >
                                    Bulk Writeoff
                                </Button>
                            ) : null}
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Bill Status</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end" width="auto">
                                    {Object.values(BillStatus).map((status) => (
                                        <DropdownItem
                                            key={status}
                                            onClick={() =>
                                                handleStatusChange(status)
                                            }
                                            isMulti
                                            itemValue={status}
                                            values={invoiceStatusFilter}
                                        >
                                            {getBillStatusFilterLabel(status)}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {cptCodeFilter || "CPT Code"}
                                    </FilterButton>
                                </DropdownTrigger>

                                <DropdownContent align="start" width="auto">
                                    <DropdownItem
                                        isSelected={!cptCodeFilter}
                                        onClick={() => handleCptCodeChange("")}
                                        className="w-[100px]"
                                    >
                                        All
                                    </DropdownItem>

                                    {Object.values(
                                        CPT_CODES.map((code) => (
                                            <DropdownItem
                                                isSelected={
                                                    cptCodeFilter === code
                                                }
                                                key={code}
                                                onClick={() =>
                                                    handleCptCodeChange(code)
                                                }
                                            >
                                                {removeEnumUnderscore(code)}
                                            </DropdownItem>
                                        ))
                                    )}
                                </DropdownContent>
                            </Dropdown>
                            <InsuranceProviderFilter
                                values={insuranceProviderIdsFilter}
                                onChange={setInsuranceProviderIdsFilter}
                                align="start"
                            />
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-2">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    {getTotalAmountOfTableRowsSelected(rowSelection).amount >
                        0 && (
                        <div className="flex items-center mt-4 mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                            <div className="font-medium text-base">
                                {
                                    getTotalAmountOfTableRowsSelected(
                                        rowSelection
                                    ).amountString
                                }
                                :
                            </div>
                            {getTotalAmountOfTableRowsSelected(rowSelection)
                                .amount === 1 && canSeeChargeButtons ? (
                                <Button
                                    onClick={() =>
                                        handleModalInView({
                                            modal: "charge_client",
                                        })
                                    }
                                >
                                    Charge Client
                                </Button>
                            ) : null}
                            {canPerformBulkActions && (
                                <>
                                    {getTotalAmountOfTableRowsSelected(
                                        rowSelection
                                    ).amount > 1 && canSeeChargeButtons ? (
                                        <Button
                                            onClick={() =>
                                                setShowChargeModal(true)
                                            }
                                        >
                                            Charge Invoices
                                        </Button>
                                    ) : null}
                                    {getSelectedData &&
                                        getSelectedData.length > 0 && (
                                            <Dropdown>
                                                <DropdownTrigger asChild>
                                                    <Button
                                                        variant="secondary"
                                                        iconRight={
                                                            <ChevronDownIcon />
                                                        }
                                                        disabled={
                                                            generatePaymentLink.isLoading
                                                        }
                                                    >
                                                        Generate Payment Link
                                                    </Button>
                                                </DropdownTrigger>
                                                <DropdownContent align="end">
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleGeneratePaymentLink(
                                                                PaymentLinkType.OPEN
                                                            )
                                                        }
                                                    >
                                                        Public Link
                                                    </DropdownItem>
                                                    <DropdownItem
                                                        onClick={() =>
                                                            handleGeneratePaymentLink(
                                                                PaymentLinkType.CLOSED
                                                            )
                                                        }
                                                    >
                                                        Private Link
                                                    </DropdownItem>
                                                </DropdownContent>
                                            </Dropdown>
                                        )}
                                    {getTotalAmountOfTableRowsSelected(
                                        rowSelection
                                    ).amount > 0 ? (
                                        <Dropdown>
                                            <DropdownTrigger asChild>
                                                <FilterButton
                                                    disabled={
                                                        updateInvoice.isLoading
                                                    }
                                                    className="bg-[var(--color-secondary)]"
                                                >
                                                    Update status
                                                </FilterButton>
                                            </DropdownTrigger>
                                            <DropdownContent
                                                width={270}
                                                align="end"
                                            >
                                                {sortedBillStatuses.map(
                                                    (status) => (
                                                        <DropdownItem
                                                            key={status}
                                                            onClick={() =>
                                                                handleBillStatusSelect(
                                                                    status
                                                                )
                                                            }
                                                        >
                                                            {getBillStatusLabel(
                                                                status
                                                            )}
                                                        </DropdownItem>
                                                    )
                                                )}
                                            </DropdownContent>
                                        </Dropdown>
                                    ) : null}
                                </>
                            )}
                        </div>
                    )}

                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "invoice_status[]",
                                values: invoiceStatusFilter,
                            },
                            {
                                key: "from_date",
                                values: fromDateFilter || "",
                            },
                            {
                                key: "to_date",
                                values: toDateFilter || "",
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                        localStorageKey="clientclaimslist_filters"
                    />
                </Card>

                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display invoices at this time please try again later`
                        }
                    />
                )}
                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Invoice list will appear here"
                        isError={false}
                        emptyMessage="There are no invoices yet"
                    />
                )}
                {isLoading && <Loader />}
                {!isLoading && isSuccess && data && data.data.length > 0 && (
                    <>
                        {clientDetails.isSuccess ? (
                            <Card classNames="mb-[--spacing-items] flex justify-between items-center">
                                <Alert
                                    type="info"
                                    description={
                                        <span>
                                            Client can be charged by
                                            <span className="font-semibold ml-1 inline-block">
                                                {clientCanBeChargedByText}
                                            </span>
                                        </span>
                                    }
                                    classNames="w-[40%]"
                                />
                                {(!canSetAnyClientBiller &&
                                    clientDetails?.data?.data
                                        ?.client_can_be_charged_by?.user_id ===
                                        loggedInUser?.data?.user_id) ||
                                (!canSetAnyClientBiller &&
                                    !clientDetails?.data?.data
                                        ?.client_can_be_charged_by?.user_id) ? (
                                    <div>
                                        {clientDetails?.data?.data
                                            ?.client_can_be_charged_by
                                            ?.user_id ===
                                        loggedInUser?.data?.user_id ? (
                                            <Button
                                                disabled={
                                                    isEditingClientDetails
                                                }
                                                onClick={() =>
                                                    handleUpdateClientDetails(
                                                        null
                                                    )
                                                }
                                            >
                                                Unassign
                                            </Button>
                                        ) : (
                                            <Button
                                                disabled={
                                                    isEditingClientDetails
                                                }
                                                onClick={() =>
                                                    handleUpdateClientDetails(
                                                        loggedInUser?.data
                                                            ?.user_id || ""
                                                    )
                                                }
                                            >
                                                Assign to me
                                            </Button>
                                        )}
                                    </div>
                                ) : null}
                                {canSetAnyClientBiller ? (
                                    <div>
                                        <p className="font-medium !text-caption text-textSec flex items-center mb-1">
                                            Assign to:
                                        </p>
                                        <Popover
                                            open={showInternalUsersListPopover}
                                            onOpenChange={
                                                setShowInternalUsersListPopover
                                            }
                                        >
                                            <PopoverTrigger asChild>
                                                <FilterButton>
                                                    {clientCanBeChargedByText}
                                                </FilterButton>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                align="end"
                                                className="w-[500x] overflow-y-auto"
                                            >
                                                <SearchInput
                                                    placeholder="Search by user's name"
                                                    onChange={(e) =>
                                                        setInternalUserSearchString(
                                                            e.target.value
                                                        )
                                                    }
                                                />

                                                <div className="flex flex-col items-start max-h-[230px] overflow-y-auto mt-2">
                                                    {internalUsersList.isLoading && (
                                                        <Loader
                                                            wrapperClassName="h-[100px] shrink-0"
                                                            className="size-[30px]"
                                                        />
                                                    )}

                                                    {internalUsersList.error && (
                                                        <ListState
                                                            isError
                                                            context="general"
                                                            stateHelperText="Please try again later"
                                                            errorMsg={
                                                                internalUsersList
                                                                    .error
                                                                    ?.response
                                                                    ?.data
                                                                    ?.message ||
                                                                "Error loading users"
                                                            }
                                                            cardClass="h-[100px] shrink-0"
                                                        />
                                                    )}

                                                    {internalUsersList.isSuccess && (
                                                        <>
                                                            <Button
                                                                size="auto"
                                                                variant="can-hover"
                                                                className=" capitalize w-full justify-between shrink-0"
                                                                onClick={() =>
                                                                    handleUpdateClientDetails(
                                                                        null
                                                                    )
                                                                }
                                                                disabled={
                                                                    isEditingClientDetails
                                                                }
                                                            >
                                                                Unassign
                                                                {!clientDetails
                                                                    ?.data?.data
                                                                    ?.client_can_be_charged_by
                                                                    ?.user_id ? (
                                                                    <CheckIcon className="stroke-textLink" />
                                                                ) : null}
                                                            </Button>

                                                            {filteredInternalUsersList.map(
                                                                (user) => (
                                                                    <Button
                                                                        key={
                                                                            user.user_id
                                                                        }
                                                                        size="auto"
                                                                        variant="can-hover"
                                                                        className=" capitalize w-full justify-between shrink-0"
                                                                        onClick={() =>
                                                                            handleUpdateClientDetails(
                                                                                user.user_id
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            isEditingClientDetails
                                                                        }
                                                                    >
                                                                        {loggedInUser
                                                                            ?.data
                                                                            ?.user_id ===
                                                                        user.user_id
                                                                            ? "Assign to me"
                                                                            : `${user.first_name} ${user.last_name} `}
                                                                        {clientDetails
                                                                            ?.data
                                                                            ?.data
                                                                            ?.client_can_be_charged_by
                                                                            ?.user_id ===
                                                                        user.user_id ? (
                                                                            <CheckIcon className="stroke-textLink" />
                                                                        ) : null}
                                                                    </Button>
                                                                )
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                ) : null}
                            </Card>
                        ) : null}
                        <Table
                            data={data.data}
                            columns={columns}
                            rowSelection={rowSelection}
                            setRowSelection={setRowSelection}
                            setGetSelectedData={setGetSelectedData}
                            expandedRows={expandedRows}
                            setExpandedRows={setExpandedRows}
                            renderRowSubComponent={renderRowSubComponentShow}
                            className={styles.billslist}
                            hasPagination
                            isHeaderFixed
                            handleRowStyle={handleRowStyle}
                            tableHeader={
                                Object.keys(expandedRows).length > 0 ? (
                                    <Button
                                        className="mb-[--spacing-items]"
                                        onClick={() => setExpandedRows({})}
                                    >
                                        Collapse all
                                    </Button>
                                ) : undefined
                            }
                            pagination={
                                <Pagination
                                    totalCount={data.total_count}
                                    totalPages={data.total_pages}
                                    currentPage={pageFilter}
                                    onPageChange={handlePageChange}
                                    limit={limitFilter}
                                    onLimitChange={handleLimitChange}
                                />
                            }
                        />
                    </>
                )}

                {canViewCreditCardInfo ? (
                    <div className="mt-[--spacing-sections]">
                        <CreditCards />
                    </div>
                ) : null}
            </div>
            {showPaymentLinkPreviewDialog && getSelectedData && (
                <Dialog open>
                    <DialogContent
                        variant="center"
                        title="Review Payment Link Details"
                        handleCloseDialog={() => {
                            setShowPaymentLinkPreviewDialog(false);
                            setSelectedLinkType(null);
                            setIsPaymentLinkFormValid(true);
                        }}
                        showFooter
                        saveText="Generate Link"
                        cancelText="Cancel"
                        submitBtnFormValue="payment-link-preview-form"
                        isSubmitBtnDisabled={
                            generatePaymentLinkIsMutating > 0 ||
                            !isPaymentLinkFormValid
                        }
                        isCancelBtnDisabled={generatePaymentLinkIsMutating > 0}
                        wrapperClass="w-[900px]"
                    >
                        <PaymentLinkPreviewForm
                            onFormSubmit={handlePreviewGenerate}
                            selectedInvoices={
                                getSelectedData.map((row) => row.original) || []
                            }
                            linkType={selectedLinkType}
                            onValidationChange={setIsPaymentLinkFormValid}
                        />
                    </DialogContent>
                </Dialog>
            )}

            <Dialog open={showPaymentLinkSuccessDialog}>
                <DialogContent
                    title="Payment Link Generated"
                    handleCloseDialog={() => {
                        setShowPaymentLinkSuccessDialog(false);
                        setPaymentLinkData(null);
                        setSelectedLinkType(null);
                    }}
                    variant="center"
                    saveText="Done"
                    onSaveClick={() => {
                        setShowPaymentLinkSuccessDialog(false);
                        setPaymentLinkData(null);
                        setSelectedLinkType(null);
                    }}
                    isCancelBtnDisabled={generatePaymentLink.isLoading}
                    isSubmitBtnDisabled={generatePaymentLink.isLoading}
                >
                    <ClientPaymentLinkGenerated
                        paymentLinkData={paymentLinkData}
                        isLoading={generatePaymentLink.isLoading}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={showPaymentLinkErrorDialog}>
                <DialogContent
                    title="Error Generating Payment Link"
                    handleCloseDialog={() => {
                        setShowPaymentLinkErrorDialog(false);
                        setPaymentLinkErrorMessage(undefined);
                    }}
                    variant="center"
                    saveText="Close"
                    submitBtnFormValue="payment-link-error"
                    isCancelBtnDisabled={false}
                    isSubmitBtnDisabled={false}
                >
                    <ListState
                        isError
                        errorMsg={
                            paymentLinkErrorMessage ||
                            `Cannot generate payment link. Please try again later`
                        }
                        listHeader="ERROR"
                        stateHelperText="Please try again later"
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}
